import { animate, motion, useMotionValue, useTransform } from "motion/react";
import { useEffect } from "react";
import { EASE, SPRING } from "@/components/motion/motion";
import { usePlayOnce } from "@/hooks/use-play-once";
import { COSTS, formatEuro } from "@/lib/costs";
import { useI18n } from "@/lib/i18n/use-i18n";
import { cn } from "@/lib/utils";

const SHRINK = { duration: 1.2, ease: EASE } as const;
const INSTANT = { duration: 0 } as const;
const ROW_STAGGER = 0.25;
const START = 0.3;

type CostRow = { key: string; label: string; before: number; after: number };

/**
 * The same software priced twice: the bronze bar starts as long as the
 * "before" bar, then shrinks to its share while its amount counts down, and
 * the factor lands. Server and reduced motion render the final amounts.
 */
export function CostCompare({
	title,
	beforeLabel,
	afterLabel,
	rows,
	factor,
	note,
	className,
}: {
	title: string;
	beforeLabel: string;
	afterLabel: string;
	rows: CostRow[];
	factor: number;
	note?: string;
	className?: string;
}) {
	const { locale } = useI18n();
	const { ref, rewound } = usePlayOnce<HTMLElement>();
	const landed = START + (rows.length - 1) * ROW_STAGGER + SHRINK.duration;

	return (
		<figure
			ref={ref}
			className={cn("rounded-3xl border bg-card p-6 md:p-8", className)}
		>
			<div className="flex items-start justify-between gap-6">
				<div className="space-y-3">
					<figcaption className="font-mono text-xs font-medium tracking-[0.06em] text-muted-foreground uppercase">
						{title}
					</figcaption>
					<ul
						aria-hidden="true"
						className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-muted-foreground"
					>
						<li className="flex items-center gap-2">
							<span className="h-2 w-4 rounded-full bg-foreground/20" />
							{beforeLabel}
						</li>
						<li className="flex items-center gap-2">
							<span className="h-2 w-4 rounded-full bg-signal" />
							{afterLabel}
						</li>
					</ul>
				</div>
				<motion.p
					aria-hidden="true"
					className="font-mono text-4xl leading-none font-semibold tracking-[-0.06em] text-signal-ink md:text-5xl"
					initial={false}
					animate={
						rewound ? { opacity: 0, scale: 0.6 } : { opacity: 1, scale: 1 }
					}
					transition={rewound ? INSTANT : { ...SPRING, delay: landed }}
				>
					÷{factor}
				</motion.p>
			</div>
			<dl className="mt-8 space-y-7">
				{rows.map((row, index) => {
					const delay = START + index * ROW_STAGGER;
					return (
						<div key={row.key}>
							<dt className="text-sm font-semibold">{row.label}</dt>
							<dd className="mt-3 grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-2">
								<span
									aria-hidden="true"
									className="h-2.5 rounded-full bg-foreground/20"
								/>
								<span className="min-w-[6rem] text-right font-mono text-xs text-muted-foreground tabular-nums line-through decoration-foreground/30">
									<span className="sr-only">{beforeLabel}: </span>
									{formatEuro(row.before, locale)}
								</span>
								<span
									aria-hidden="true"
									className="h-2.5 rounded-full bg-muted"
								>
									<motion.span
										className="block h-full origin-left rounded-full bg-signal"
										initial={false}
										animate={{ scaleX: rewound ? 1 : row.after / row.before }}
										transition={rewound ? INSTANT : { ...SHRINK, delay }}
									/>
								</span>
								<span className="min-w-[6rem] text-right font-mono text-sm font-semibold tabular-nums">
									<span className="sr-only">{afterLabel}: </span>
									<Amount
										from={row.before}
										to={row.after}
										rewound={rewound}
										delay={delay}
									/>
								</span>
							</dd>
						</div>
					);
				})}
			</dl>
			{note ? (
				<p className="mt-8 border-t pt-4 text-xs text-pretty text-muted-foreground">
					{note}
				</p>
			) : null}
		</figure>
	);
}

/** An amount that counts down from `from` to `to` alongside its bar. */
function Amount({
	from,
	to,
	rewound,
	delay,
}: {
	from: number;
	to: number;
	rewound: boolean;
	delay: number;
}) {
	const { locale } = useI18n();
	const value = useMotionValue(to);
	const text = useTransform(value, (current) =>
		formatEuro(Math.round(current / 100) * 100, locale),
	);

	useEffect(() => {
		if (rewound) {
			value.jump(from);
			return;
		}
		const controls = animate(value, to, { ...SHRINK, delay });
		return () => controls.stop();
	}, [rewound, from, to, delay, value]);

	return <motion.span>{text}</motion.span>;
}

/** Custom business software, classic build vs AI-assisted build. */
export function BusinessAppCost({ className }: { className?: string }) {
	const { dict } = useI18n();
	const copy = dict.economics.businessApp;
	const { build, yearly, factor } = COSTS.businessApp;

	return (
		<CostCompare
			className={className}
			title={copy.title}
			beforeLabel={copy.before}
			afterLabel={copy.after}
			factor={factor}
			note={dict.economics.note}
			rows={[
				{ key: "build", label: copy.rows.build, ...build },
				{ key: "yearly", label: copy.rows.yearly, ...yearly },
			]}
		/>
	);
}

/** Legacy React Native app vs the same app on Expo. */
export function ExpoCost({ className }: { className?: string }) {
	const { dict } = useI18n();
	const copy = dict.economics.expo;
	const { yearly, feature, factor } = COSTS.expo;

	return (
		<CostCompare
			className={className}
			title={copy.title}
			beforeLabel={copy.before}
			afterLabel={copy.after}
			factor={factor}
			note={dict.economics.note}
			rows={[
				{ key: "yearly", label: copy.rows.yearly, ...yearly },
				{ key: "feature", label: copy.rows.feature, ...feature },
			]}
		/>
	);
}
